<template>
	<div class="container-fluid">
		<div class="row">
			<div class="col-xxs-12 col-md-6">
				<ColorMixer
					:scroll-to-mixer="scrollToMixer"
					:color-data="selectedColors"
				/>
				<LearnMoreLink
					:link="learnMoreLink"
				/>
				<SelectRubberType
					:active-rubber-type="activeRubberType"
					:rubber-types="rubberTypes"
					@rubber-type:change="handleRubberTypeChange"
				/>
			</div>
			<div class="col-xxs-12 col-md-6">
				<div class="container-fluid">
					<div class="row">
						<div class="col-xxs-12">
							<AllBreakouts
								:active-colors="activeColors"
								:selected-colors="selectedColors"
								:flyout-instructions="flyoutInstructions"
								@breakout:select:color="handleBreakoutSelectColor"
								@breakout:change:color="handleBreakoutChangeColor"
								@breakout:update:percentage="handleBreakoutUpdatePercentage"
								@breakout:trash="handleBreakoutTrashColor"
								@breakouts:percentages:equalize="handleBreakoutsEqualizePercentages"
								@breakouts:view:mixer="handleBreakoutsViewMixer"
							/>
						</div>
					</div>
					<div class="row">
						<div class="col-xxs-12">
							<div class="supporting-btns">
								<BtnGetAQuote
									:color-data="selectedColors"
								/>
								<BtnDownloadColorMix
									:color-data="selectedColors"
									:site-logo-url="siteLogoUrl"
									:pdf-text-disclaimer="pdfTextDisclaimer"
									:pdf-text-contact="pdfTextContact"
								/>
							</div>
						</div>
					</div>
					<div class="row">
						<div class="col-xxs-12">
							<DisclaimerAside
								:text="disclaimerText"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>


<style lang="scss">
.color-mixer {
	margin-bottom: 32px;

	@media( min-width: 1024px ) {
		margin-top: 16px;
		margin-bottom: 48px;
	}
}

.supporting-btns {
	text-align: center;
}
</style>


<script setup>
import { ref } from 'vue'

import ColorMixer from './components/ColorMixer.vue'

import AllBreakouts from './components/AllBreakouts.vue'

import BtnGetAQuote from './components/BtnGetAQuote.vue'
import BtnDownloadColorMix from './components/BtnDownloadColorMix.vue'
import SelectRubberType from './components/SelectRubberType.vue'

import DisclaimerAside from './components/DisclaimerAside.vue'
import LearnMoreLink from './components/LearnMoreLink.vue'


const rubberTypes = ref( window.webfx.colorMixer.addonData.rubber_types )
const activeRubberType = ref( 'EPDM' ) // todo -- make dynamic through WP admin

const scrollToMixer = ref( 0 ) // todo -- really hacky, gotta be better way

// let's get all data from global var here (versus in individual components)
const disclaimerText = ref( window.webfx.colorMixer.addonData.text?.disclaimer_your_mix ?? '' )
const learnMoreLink = ref( window.webfx.colorMixer.addonData.links?.learn_more ?? {})
const siteLogoUrl = ref( window.webfx.colorMixer.addonData.images?.site_logo ?? '' )
const pdfTextDisclaimer = ref( window.webfx.colorMixer.addonData.text?.pdf_disclaimer ?? '' )
const pdfTextContact = ref( window.webfx.colorMixer.addonData.text?.pdf_contact_info ?? '' )
const flyoutInstructions = ref( window.webfx.colorMixer.addonData.text?.flyout_instructions ?? '' )


/**
 * Parse color data
 *
 * @return {obj}
 */
// eslint-disable-next-line arrow-body-style
const getAllColorData = colorData => {
	return colorData.map( color => {
		color.percentage = 0
		color.placementIndex = null

		return color
	})
}
const allColorData = ref( getAllColorData( window.webfx.colorMixer.addonData.colors ) )


/**
 * Get colors that are assigned to active rubber type
 *
 * @return {array}
 */
// eslint-disable-next-line arrow-body-style
const getColorsByActiveRubberType = () => {
	return allColorData.value.filter( color => color.rubber_types.includes( activeRubberType.value ) )
}
const activeColors = ref( getColorsByActiveRubberType() )


/**
 * Get currently selected colors
 *
 * @param {boolean} resetPlacementIndexes
 * @param {boolean} resetPercentages
 * @return {void}
 */
const getSelectedColors = () => {

	// eslint-disable-next-line arrow-body-style
	const colors = activeColors.value.filter( color => {
		return color.selected && color.rubber_types.includes( activeRubberType.value )
	})

	// sort by index
	colors.sort( ( a, b ) => a.placementIndex - b.placementIndex )

	return colors
}


/**
 * Process selected colors by equalizing percentages and setting initial placement indices
 *
 * @param {boolean} resetPlacementIndexes
 * @param {boolean} resetPercentages
 * @return {void}
 */
const initializeSelectedColors = () => {
	let selectedColors = getSelectedColors()
	let placementIndex = 0

	// add percentages and indices
	selectedColors = selectedColors.map( color => {

		// eslint-disable-next-line no-plusplus
		color.placementIndex = placementIndex++

		color.percentage = Math.floor( 100 / selectedColors.length )

		return color
	})

	// sort by placement index
	selectedColors.sort( ( a, b ) => a.placementIndex - b.placementIndex )

	return selectedColors
}
const selectedColors = ref( initializeSelectedColors() )


/**
 * Update selected colors variable with latest selected color data
 *
 * @return {void}
 */
const updateSelectedColorsData = () => {
	selectedColors.value = getSelectedColors()
}


/**
 * Add new color to selected colors
 *
 * @param {number} selectedColorId
 * @param {number} percentage
 * @param {number} placementIndex
 *
 * @return {void}
 */
const addSelectedColor = ( selectedColorId, percentage = 50, placementIndex = null ) => {
	const match = activeColors.value.find( color => selectedColorId === color.id )

	if ( !match ) {
		return
	}

	match.selected = true
	match.percentage = percentage

	// todo - check if placement ID is already being used
	if ( null === placementIndex ) {
		placementIndex = selectedColors.value.filter( color => color.selected ).length
	}

	match.placementIndex = placementIndex
}


/**
 * Add new color to selected colors
 *
 * @param {number} colorId
 * @return {void}
 */
const deleteSelectedColor = colorId => {

	// update master data
	const match = activeColors.value.find( color => colorId === color.id )

	if ( match ) {
		match.selected = false
		match.placementIndex = null
	}

	// todo -- reset placement indexes
}


/**
 * Handle user selecting color for new breakout
 *
 * This color will appear at the END of the grid of colors.
 *
 * @param {number} selectedColorId
 * @return {void}
 */
const handleBreakoutSelectColor = selectedColorId => {
	const remainder = 100 - selectedColors.value.reduce( ( total, color ) => total + color.percentage, 0 )

	// if valid remainder exists, use that for the new color
	if ( ( 0 < remainder && 100 > remainder ) && ( 99 !== remainder && 3 !== selectedColors.value.length ) ) {
		addSelectedColor( selectedColorId, remainder )

	// otherwise, equally split the percentage between all colors
	} else {
		addSelectedColor( selectedColorId, 0 )

		let colors = getSelectedColors()

		colors = colors.map( color => {
			color.percentage = Math.floor( 100 / colors.length )

			return color
		})
	}

	updateSelectedColorsData()
}


/**
 * Handle user updating percentage for selected color
 *
 * @param {number} colorId
 * @param {number} percentage
 *
 * @return {void}
 */
const handleBreakoutUpdatePercentage = ( colorId, percentage ) => {
	const match = activeColors.value.find( color => color.id === colorId )

	if ( match ) {
		match.percentage = percentage
	}

	// if only two active colors, update opposite color to show remainder
	if ( 2 === getSelectedColors().length ) {
		const oppositeColor = selectedColors.value.find( color => color.id !== colorId )

		if ( oppositeColor ) {
			oppositeColor.percentage = 100 - percentage
		}
	}

	// todo -- need to run updateSelectedColorsData?
}


/**
 * Handle user clearing breakout
 *
 * @param {number} colorId
 * @return {void}
 */
const handleBreakoutTrashColor = colorId => {
	const match = activeColors.value.find( color => color.id === colorId )

	if ( match ) {
		match.selected = false
		match.percentage = 0
	}

	// remove selected color
	selectedColors.value = selectedColors.value.filter( color => color.id !== colorId )

	// try to re-distriubte percentages
	const remainder = 100 - getSelectedColors().reduce( ( total, color ) => total + color.percentage, 0 )

	if ( 0 < remainder && 100 > remainder ) {
		selectedColors.value = selectedColors.value.map( color => {
			color.percentage += Math.floor( remainder / selectedColors.value.length )

			return color
		})
	}

	updateSelectedColorsData()
}


/**
 * Handle user equalizing percentages for active breakouts
 *
 * @return {void}
 */
const handleBreakoutsEqualizePercentages = () => {
	const totalColors = selectedColors.value.length

	selectedColors.value = selectedColors.value.map( color => {
		color.percentage = Math.floor( 100 / totalColors )

		return color
	})

	// updateSelectedColorsData()
}


/**
 * Handle user clicking to view mixer on mobile
 *
 * @return {void}
 */
const handleBreakoutsViewMixer = () => {
	scrollToMixer.value += 1
}


/**
 * Handle user selecting different color for (already active breakout)
 *
 * @param {number} newColorId
 * @param {number} placementIndex
 * @param {number} percentage
 * @param {number} oldColorId
 *
 * @return {void}
 */
const handleBreakoutChangeColor = ( newColorId, placementIndex, percentage, oldColorId ) => {
	deleteSelectedColor( oldColorId )
	addSelectedColor( newColorId, percentage, placementIndex )

	// will update components
	updateSelectedColorsData()
}


/**
 * Handle user selecting rubber type
 *
 * @param {string} rubberType
 * @return {void}
 */
const handleRubberTypeChange = rubberType => {
	activeRubberType.value = rubberType
	activeColors.value = getColorsByActiveRubberType()

	// do we need to initialize colors for rubber type?
	const totalPercentage = activeColors.value.reduce( ( total, color ) => total + color.percentage, 0 )
	if ( 0 === totalPercentage ) {
		selectedColors.value = initializeSelectedColors()
	}

	// will update components
	updateSelectedColorsData()
}

</script>
